import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { AppUser } from '../../../../views/App/App';
import { FILTER_TYPE, FIRST_PAGE } from '../../../../consts/table';
import { Grid2 } from '../../../../components/Grid/Grid2';
import { Loader } from '../../../../components/Loader/Loader';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import {
  ColumnDefinition,
  getFilters2,
  isFilterExist2,
  isSortExist,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  TABLE_SORT_DIRECTION
} from '../../../../helpers/table/table';
import {
  getTournament,
  getTournamentAthleteSchools,
  getTournamentAthleteSchoolsCount
} from '../../../../helpers/service/adminSU/tournamentsTable';
import { getAllSchoolUnionSchools } from '../../../../helpers/service/adminSU/schoolsTable';
import { KIND, SCHOOL_GENDER } from '../../../../consts/school';
import {
  getSchoolAvailableAgesGroup,
  getSchoolAvailableGender,
  getTournamentSchoolAreas
} from '../../../../helpers/accessor/accessor';
import {
  getSelectOptionForAreaSU,
  getSelectOptionForAvailableAges,
  getSelectOptionForAvailableGender
} from '../../../../helpers/table/select';
import {
  createTournamentNotification,
  scheduleTournamentNotification,
  sendTournamentNotificationNow
} from '../../../../helpers/service/adminSU/tournamentNotificationTable';
import { Tournament } from '../../../../models/tournament';
import { TournamentEligibleSchoolsCustomNotificationsView } from './TournamentEligibleSchoolsCustomNotificationsView/TournamentEligibleSchoolsCustomNotificationsView';
import './TournamentNotificationWizard.scss';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Eligible genders',
    field: 'availableGender',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getSchoolAvailableGender
  },
  {
    text: 'Eligible ages',
    field: 'availableAges',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getSchoolAvailableAgesGroup
  },
  {
    text: 'Area',
    field: 'areaSU',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getTournamentSchoolAreas
  },
  {
    text: 'Athletes',
    field: 'athleteCount',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: item => String(item.athleteCount || 0)
  }
];

interface AthleteSchoolItem {
  id: string;
  name: string;
  pic?: string;
  availableAges: number[];
  availableGender: string;
  areaSU: any[];
  athleteCount: number;
  isParticipant: boolean;
}

interface State {
  items: AthleteSchoolItem[];
  allItems: AthleteSchoolItem[];
  currentPage: number;
  selectedItems: AthleteSchoolItem[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  isCustomNotificationModalShow: boolean;
  isError: boolean;
  tournament: Tournament;
  schoolUnions: any[];
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

const PAGE_SIZE = 20;

export class TournamentAthleteSchools extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      allItems: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      isCustomNotificationModalShow: false,
      isError: false,
      tournament: undefined,
      schoolUnions: []
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    this.loadData();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true });
      this.applyFiltersAndPaging();
    }
  }

  getTournamentId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    return search.tournament as string;
  }

  loadData() {
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    BPromise.all([
      getTournament(user, tournamentId),
      getAllSchoolUnionSchools(user, { kind: { $in: [KIND.SCHOOL_UNION] } })
    ]).then(([tournament, schoolUnions]) => {
      this.setState(
        { tournament, schoolUnions, basePath: this.props.history.location.pathname },
        () => this.applyFiltersAndPaging()
      );
    }).catch(error => {
      console.error(error);
      this.setState({ isError: true, isLoading: false });
    });
  }

  getAthleteSchoolsWhere = (filters: any): any => {
    const { areaSU, ...serverFilters } = filters || {};
    const where = getServerFieldSectionWhere(serverFilters);

    if (filters.availableGender === 'MALE_ONLY') {
      where.availableGender = { $in: [SCHOOL_GENDER.MALE, SCHOOL_GENDER.MIXED] };
    } else if (filters.availableGender === 'FEMALE_ONLY') {
      where.availableGender = { $in: [SCHOOL_GENDER.FEMALE, SCHOOL_GENDER.MIXED] };
    }

    if (Array.isArray(areaSU) && areaSU.length > 0) {
      const selectedAreaIds = areaSU.map(String);
      const schoolIds = this.state.schoolUnions
        .filter(area => selectedAreaIds.includes(String(area.id)))
        .reduce<string[]>((result, area) => result.concat((area.schoolIds || []).map(String)), []);
      where.id = { $in: Array.from(new Set(schoolIds)) };
    }

    return where;
  };

  mapAthleteSchool = (school: any): AthleteSchoolItem => ({
    id: String(school.id),
    name: school.name,
    pic: school.pic,
    availableAges: school.availableAges || [],
    availableGender: school.availableGender || '',
    areaSU: this.state.schoolUnions.filter(area =>
      (area.schoolIds || []).map(String).includes(String(school.id))
    ),
    athleteCount: school.athleteCount || 0,
    isParticipant: true
  });

  applyFiltersAndPaging() {
    const { history } = this.props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';
    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);
    const where = this.getAthleteSchoolsWhere(filters);
    const order = sortColumnsName && sortDirection ? `${sortColumnsName} ${sortDirection}` : 'name ASC';
    const requestFilter = getServerQueryFilter2(page, where, order, PAGE_SIZE);
    const tournamentId = this.getTournamentId();

    return BPromise.all([
      getTournamentAthleteSchools(this.props.user, tournamentId, true, requestFilter),
      getTournamentAthleteSchoolsCount(this.props.user, tournamentId, true, where)
    ])
      .then(([athleteSchools, count]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items: athleteSchools.map(this.mapAthleteSchool),
          total: typeof count === 'number' ? count : count.count,
          isLoading: false
        });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isError: true, isLoading: false });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const { filters, sortColumnsName, sortDirection } = this.state;
    const { history } = this.props;
    const search = [];

    if (currentPage !== 1) search.push(`page=${currentPage}`);
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));

    const tournamentId = this.getTournamentId();
    history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const updated = [...selectedItems];
    if (selectedItemIndex !== -1) {
      updated.splice(selectedItemIndex, 1);
    } else {
      updated.push(selectedItem);
    }
    this.setState({ selectedItems: updated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const newItems = items.filter(item => selectedItems.every(s => s.id !== item.id));
    this.setState({ selectedItems: [...selectedItems, ...newItems], isSelectAllChecked: true });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const tournamentId = this.getTournamentId();
    const where = this.getAthleteSchoolsWhere(filters);
    this.setState({ isLoading: true });
    getTournamentAthleteSchools(this.props.user, tournamentId, true, { where, limit: 0, order: 'name ASC' })
      .then(items => {
        const filtered = items.map(this.mapAthleteSchool);
        const newItems = filtered.filter(item => selectedItems.every(selected => selected.id !== item.id));
        this.setState({ selectedItems: [...selectedItems, ...newItems], isSelectAllChecked: true, isLoading: false });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isError: true, isLoading: false });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false, isShowFilter: false });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const { history } = this.props;
    const tournamentId = this.getTournamentId();

    const getOrder = (field, dir, col) => {
      if (field === col) return dir === 'asc' ? 'desc' : 'asc';
      return 'asc';
    };
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search = [`order=${sortField}:${order}`];
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));

    history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    const col = COLUMNS.find(c => c.field === filterField);
    if (col && col.type === FILTER_TYPE.MULTISELECT) {
      const selectOptions = event.target.options;
      const value = [];
      for (let i = 0; i < selectOptions.length; i++) {
        if (selectOptions[i].selected) value.push(selectOptions[i].value);
      }
      nextFilters = { ...nextFilters, [filterField]: value };
    } else {
      nextFilters = { ...nextFilters, [filterField]: filterValue };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortColumnsName, sortDirection } = this.state;
    const { history } = this.props;
    const tournamentId = this.getTournamentId();
    const search = [];
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(filters)) search.push(getSearchFilter(filters));
    this.setState({ selectedItems: [], isSelectAllChecked: false, isShowFilter: false, filters: {} });
    history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onClearFilterClick = (): void => {
    const { history } = this.props;
    const tournamentId = this.getTournamentId();
    this.setState({ selectedItems: [], isSelectAllChecked: false });
    history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}`,
      state: history.location.state
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState(prev => ({ isShowFilter: !prev.isShowFilter }));
  };

  onCancelFilterClick = (): void => {
    this.setState({
      isShowFilter: false,
      filters: getFilters2(COLUMNS, parse(this.props.history.location.search))
    });
  };

  goBack = () => {
    const { history, location } = this.props;
    const { state } = location;
    const { search, prevPath } = state as any;
    const tournamentId = this.getTournamentId();
    history.push({ pathname: prevPath, search, state: { tournamentId } });
  };

  onCloseErrorClick = () => this.setState({ isError: false });

  openCustomNotificationModal = () => this.setState({ isCustomNotificationModalShow: true });
  closeCustomNotificationModal = () => this.setState({ isCustomNotificationModalShow: false });

  onSubmitCustomNotifications = data => {
    const { user } = this.props;
    const { tournament } = this.state;
    this.setState({ isLoading: true });

    const { submitAction, scheduleAt, ...payload } = data || {};
    let actionDescriptorId = '';

    createTournamentNotification(user, tournament.id, payload)
      .then(response => {
        actionDescriptorId = propz.get(response, ['headers', 'action-descriptor-id'], '');
        if (!actionDescriptorId) throw new Error('actionDescriptorId is missing');
        if (submitAction === 'SCHEDULE') {
          return scheduleTournamentNotification(user, tournament.id, actionDescriptorId, scheduleAt);
        }
        return sendTournamentNotificationNow(user, tournament.id, actionDescriptorId);
      })
      .then(() => {
        this.setState({ isLoading: false, isCustomNotificationModalShow: false });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false, isError: true });
      });
  };

  renderCustomNotificationModal() {
    const { isCustomNotificationModalShow, selectedItems, tournament } = this.state;
    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={isCustomNotificationModalShow}
        title={'Custom notifications'}
        customClass="tTournamentNotificationWizardModal"
      >
        <TournamentEligibleSchoolsCustomNotificationsView
          user={user}
          onSubmit={this.onSubmitCustomNotifications}
          onCancel={this.closeCustomNotificationModal}
          schools={selectedItems as any}
          tournament={tournament}
        />
      </SimpleModal>
    );
  }

  renderErrorModal() {
    return (
      <SimpleModal
        isOpen={this.state.isError}
        title={'Error'}
        body={'Something went wrong. Please try again.'}
        onCloseClick={this.onCloseErrorClick}
        buttonCancelText={'Ok'}
      />
    );
  }

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      tournament,
      schoolUnions
    } = this.state;
    const { user } = this.props;

    const actionItems = [
      {
        itemText: 'Send notifications',
        onItemClick: this.openCustomNotificationModal,
        isActive: selectedItems.length > 0
      }
    ];

    const filterOptions = {
      availableAges: getSelectOptionForAvailableAges(),
      availableGender: getSelectOptionForAvailableGender(),
      areaSU: getSelectOptionForAreaSU(schoolUnions)
    };

    const gridTitle = typeof tournament !== 'undefined' ? `${tournament.name} / Athlete schools` : '';

    return (
      <Grid2
        user={user}
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={gridTitle}
        goBack={this.goBack}
      />
    );
  }

  render() {
    const { isLoading, isError, isCustomNotificationModalShow } = this.state;

    if (isLoading) return <Loader />;

    const classes = isError || isCustomNotificationModalShow ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {this.renderErrorModal()}
        {this.renderCustomNotificationModal()}
        <div className="row">
          <div className="col-md-12">{this.renderGrid()}</div>
        </div>
      </div>
    );
  }
}
